import { Arr, assert, lazy, memone } from '@deqo/foundation';
import { ShapeInfo } from 'kld-intersections';
import { Geometry, IntersectResult } from './Geometry';
import { Matrix } from './Matrix';
import { HasPoints, Point } from './Point';
import { Rect } from './Rect';

const utilitySvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
utilitySvg.setAttributeNS(null, 'width', '500');
utilitySvg.setAttributeNS(null, 'height', '500');
utilitySvg.setAttributeNS(null, 'viewBox', '0 0 500 500');
utilitySvg.style.position = 'absolute';
utilitySvg.style.zIndex = '10000';
utilitySvg.style.pointerEvents = 'none';

// const utilityGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
// utilityGroup.setAttributeNS(null, 'transform', 'translate(100, 100)');
// utilitySvg.appendChild(utilityGroup);

document.body.appendChild(utilitySvg);

export type PathCommand =
    ['M' | 'm' | 'L' | 'l', Point] |
    ['Q' | 'q', Point, Point] |
    ['C' | 'c', Point, Point, Point] |
    ['S' | 's', Point, Point] |
    ['T' | 't', Point] |
    ['A' | 'a', Point, number, number, number, 0 | 1, 0 | 1] |
    ['Z']
    ;

export class Path implements Geometry, Iterable<PathCommand> {

    public static ellipse(cx: number, cy: number, rx: number, ry: number) {
        // Impl based on:
        // https://stackoverflow.com/questions/59011294/ellipse-to-path-convertion-using-javascript
        // Magic number that allows us to closely appromixate an arc with cubic bezier
        const kappa = 0.5522847498307933984022516322796;
        const ox = rx * kappa; // x offset for the control point
        const oy = ry * kappa; // y offset for the control point
        return new Path([
            ['M', new Point(cx - rx, cy)],
            ['C', new Point(cx - rx, cy - oy), new Point(cx - ox, cy - ry), new Point(cx, cy - ry)],
            ['C', new Point(cx + ox, cy - ry), new Point(cx + rx, cy - oy), new Point(cx + rx, cy)],
            ['C', new Point(cx + rx, cy + oy), new Point(cx + ox, cy + ry), new Point(cx, cy + ry)],
            ['C', new Point(cx - ox, cy + ry), new Point(cx - rx, cy + oy), new Point(cx - rx, cy)],
            ['Z'],
        ]);
    }

    public static from(hp: HasPoints): Path {
        const points = Point.manyFrom(hp);
        const commands = points.map((x, i) => [i === 0 ? 'M' : 'L', x]) as PathCommand[];
        return new Path(commands.concat([['Z']]));
    }

    public static parse(str: string) {
        // Parse svg path into PathCommand

        // Insert space before/after all command type tokens
        str = str.replace(/([MmLlQqCcSsAaZ])/g, ' $1 ');

        const commands = [] as PathCommand[];
        const tokens = str.split(' ').filter(x => !!x);

        for (let i = 0; i < tokens.length; i++) {
            const type = tokens[i];
            if (type === 'Z') {
                commands.push(['Z']);
                continue;
            }
            if (type === 'M' || type === 'm' || type === 'L' || type === 'l') {
                const x = parseFloat(tokens[++i]);
                const y = parseFloat(tokens[++i]);
                commands.push([type, new Point(x, y)]);
                continue;
            }
            if (type === 'Q' || type === 'q') {
                const x1 = parseFloat(tokens[++i]);
                const y1 = parseFloat(tokens[++i]);
                const x = parseFloat(tokens[++i]);
                const y = parseFloat(tokens[++i]);
                commands.push([type, new Point(x1, y1), new Point(x, y)]);
                continue;
            }
            if (type === 'C' || type === 'c') {
                const x1 = parseFloat(tokens[++i]);
                const y1 = parseFloat(tokens[++i]);
                const x2 = parseFloat(tokens[++i]);
                const y2 = parseFloat(tokens[++i]);
                const x = parseFloat(tokens[++i]);
                const y = parseFloat(tokens[++i]);
                commands.push([type, new Point(x1, y1), new Point(x2, y2), new Point(x, y)]);
                continue;
            }
            if (type === 'S' || type === 's') {
                const x2 = parseFloat(tokens[++i]);
                const y2 = parseFloat(tokens[++i]);
                const x = parseFloat(tokens[++i]);
                const y = parseFloat(tokens[++i]);
                commands.push([type, new Point(x2, y2), new Point(x, y)]);
                continue;
            }
            if (type === 'T' || type === 't') {
                const x = parseFloat(tokens[++i]);
                const y = parseFloat(tokens[++i]);
                commands.push([type, new Point(x, y)]);
                continue;
            }
            if (type === 'A' || type === 'a') {
                const rx = parseFloat(tokens[++i]);
                const ry = parseFloat(tokens[++i]);
                const rot = parseFloat(tokens[++i]);
                const large = parseFloat(tokens[++i]);
                const sweep = parseFloat(tokens[++i]);
                const x = parseFloat(tokens[++i]);
                const y = parseFloat(tokens[++i]);
                commands.push([type, new Point(x, y), rx, ry, rot, large as any, sweep as any]);
                continue;
            }
            throw new Error(`Path.parse: unknown command '${type}'.`);
        }

        return new Path(commands);
    }

    public static builder(): PathBuilder {
        return new PathBuilder();
    }

    public readonly commands: readonly PathCommand[];

    constructor(commands: readonly PathCommand[]) {
        assert(commands[0] && (commands[0][0] === 'M' || commands[0][0] === 'Z'), 'Path: first command must be M or Z.');
        this.commands = commands;
    }

    public get closed() {
        return this.commands.length && this.commands[this.commands.length - 1][0] === 'Z';
    }

    @lazy()
    public get length() {
        return this.svgGeom.getTotalLength();
    }

    @lazy()
    public get svgGeom() {
        const geom = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        geom.setAttributeNS(null, 'd', this.toString());
        geom.setAttributeNS(null, 'fill', this.closed ? 'black' : 'none');
        geom.setAttributeNS(null, 'stroke', 'black');
        geom.setAttributeNS(null, 'stroke-width', '0');
        return geom;
    }

    // @memone()
    public bounds(strokeWidth: number = 0) {
        return this.useGeomWithStroke(strokeWidth, g => {
            const { x, y, width, height } = g.getBBox();
            return new Rect(x, y, width, height).inflate([strokeWidth / 2, strokeWidth / 2]);
        });
    }

    public pointAtLength(length: number) {
        if (isNaN(length)) length = this.length;
        return Point.from(this.svgGeom.getPointAtLength(length));
    }

    public pointAt(perc: number) {
        return this.pointAtLength(this.length * perc);
    }

    public sample(n: number) {
        n = Math.round(n);

        return [
            ...Array.from({ length: n }).map((_, i) => this.pointAt(i / (n - 1))),
            // this.pointAt(1),
        ];
    }

    public concat(...paths: Path[]): Path {
        return new Path([this, ...paths].flatMap(x => x.commands));
    }

    public contains(point: Point, strokeWidth?: number): boolean {
        return this.useGeomWithStroke(strokeWidth ?? 0, g => {
            const sp = utilitySvg.createSVGPoint();
            sp.x = point.x;
            sp.y = point.y;
            return g.isPointInStroke(sp) || (this.closed && g.isPointInFill(sp));
        });
    }

    // public nearestPoint(to: Point): Point | null {
    //     const pc = this.bounds.center();
    //     const tv = Point.vector(pc, to).normalize();
    //     const tn = tv.multiply(Math.max(this.bounds.width, this.bounds.height) * 10);

    //     const hits = this.intersectPoints(new Line(pc, tn));
    //     return !!hits && hits.length ? to.nearestOf(hits) : null;
    // }

    public intersects(another: Geometry): boolean {
        return !!this.intersectPoints(another);
    }

    public intersectPoints(another: Geometry): IntersectResult {
        return Geometry.intersect(this, another);
    }

    public scale(sx: number, sy?: number): Path {
        return this.transform(Matrix.scale(sx, sy ?? sx));
    }

    /**
     * Applies the specified Matrix to the data of this Path and returns a new Path with the transformation result.
     *
     * @param mt the matrix.
     */
    public transform(mt: Matrix): Path {
        return new Path(this.commands.map((c: any[]) =>
            c.map(x => x instanceof Point ? mt.apply(x) : x) as PathCommand
        ));
    }

    public toClosed() {
        const c = this.commands;
        if (Arr.last(c)[0] === 'Z') return this;
        return new Path([...c, ['Z']]);
    }

    public toRounded(method: 'quadratic' | 'cubic', factor: number | number[]) {
        if (Array.isArray(factor) && !factor.length) return this;
        if (!factor || Number(factor) < 0) return this;

        const fx = Array.isArray(factor)
            ? (i: number) => factor[i] ?? 0
            : (_i: number) => factor as number;

        // Magic number that allows us to closely appromixate an arc with cubic bezier
        const kappa = 0.5522847498307933984022516322796;

        const cmds = [] as PathCommand[];

        // We can only round where M/L/L or L/L/L
        const f = ['M', 'L'];

        for (let i = 0; i < this.commands.length; i++) {
            const [at, a] = Arr.at(this.commands, i);
            const [bt, b] = Arr.at(this.commands, i + 1);
            const [ct, c] = Arr.at(this.commands, i + 2);

            if (f.includes(at) && f.includes(bt) && f.includes(ct)) {
                const ab = Point.vector(a, b);
                const bc = Point.vector(b, c);
                const l = Math.min(ab.length() / 2, bc.length() / 2, fx(i));

                const sp = b.add(Point.vector(b, a).normalize().multiply(l));
                const ep = b.add(Point.vector(b, c).normalize().multiply(l));

                cmds.push([at, sp] as any);
                if (method === 'quadratic') {
                    cmds.push(['Q', b, ep]);
                }
                else {
                    const cp1 = sp.add(Point.vector(sp, b).normalize().multiply(l * kappa));
                    const cp2 = ep.add(Point.vector(ep, b).normalize().multiply(l * kappa));
                    cmds.push(['C', cp1, cp2, ep]);
                }
            }
            else {
                cmds.push(this.commands[i]);
            }

        };

        return new Path(cmds);
    }

    @memone()
    public toShapeInfo() {
        return ShapeInfo.path(this.toString());
    }

    @memone()
    public toString(leaveOpen?: boolean): string {

        const path = [] as string[];

        // eslint-disable-next-line prefer-const
        for (let [type, ...args] of this) {
            path.push(type);

            if (type === 'Z' && leaveOpen) {
                continue;
            }

            if (type === 'A' || type === 'a') {
                // Because of the way we store this, we need to jiggle the args around
                args = [...args.slice(1), args[0]] as any;
            }

            path.push(...args.map(a => a instanceof Point ? a.format('svg') : a));
        }

        return path.join(' ');
    }

    @memone()
    public toPath2D() {
        return new Path2D(this.toString());
    }

    private useGeomWithStroke<T>(strokeWidth: number, fn: (geom: SVGGeometryElement) => T) {
        // utilitySvg.removeChild(this.svgGeom);
        // document.body.removeChild(utilitySvg);
        // Remove all children from utilityG

        if (!this.svgGeom.parentElement) {
            utilitySvg.appendChild(this.svgGeom);
        }

        // document.body.appendChild(utilitySvg);
        try {
            this.svgGeom.setAttributeNS(null, 'stroke-width', `${strokeWidth}`);
            return fn(this.svgGeom);
        }
        finally {
            utilitySvg.removeChild(this.svgGeom);
            // document.body.removeChild(utilitySvg);
        }
    }

    [Symbol.iterator](): Iterator<PathCommand> {
        return this.commands[Symbol.iterator]();
    }
}

class PathBuilder {

    public readonly commands: PathCommand[] = [];

    public get empty() {
        return this.commands.length === 0;
    }

    public moveTo(p: Point): this {
        this.commands.push(['M', p]);
        return this;
    }

    public cubeTo(cp1: Point, cp2: Point, p: Point): this {
        this.commands.push(['C', cp1, cp2, p]);
        return this;
    }

    public continueCubeTo(cp2: Point, p: Point): this {
        this.commands.push(['S', cp2, p]);
        return this;
    }

    public quadTo(cp: Point, p: Point, continuation?: Point[]): this {
        this.commands.push(['Q', cp, p]);
        for (const x of continuation ?? []) {
            this.commands.push(['T', x]);
        }
        return this;
    }

    public continueQuadTo(p: Point): this {
        this.commands.push(['T', p]);
        return this;
    }

    public lineTo(p: Point | Point[]): this {
        p = Array.isArray(p) ? p : [p];
        for (const x of p) {
            this.commands.push(['L', x]);
        }
        return this;
    }

    public arcTo(p: Point, [rx, ry, laf, sf]: [number, number, 0 | 1, 0 | 1]): this {
        this.commands.push(['A', p, rx, ry, 0, laf, sf]);
        return this;
    }

    public addPath(path: Path): this {
        this.commands.push(...path.commands.filter(x => x[0] !== 'Z'));
        return this;
    }

    public build(close?: boolean): Path {
        if (close) {
            this.commands.push(['Z']);
        }
        return new Path(this.commands);
    }
}
